// SPDX-License-Identifier: AGPL-3.0-or-later
// A made-up household for demo mode. Nothing here is real, and nothing is saved.
import { addDays, addMonths, isoDate, occurrences, weekday, yearOf, type IsoDate } from "@almanac/domain";
import { categorize, STARTER_RULES } from "@almanac/importers";
import {
  DEFAULT_FEED,
  DEFAULT_NOTIFY,
  defaultPlan,
  ME,
  type Account,
  type CalendarEvent,
  type Group,
  type GroupPayment,
  type MonthBundle,
  type Purchase,
  type Snapshot,
  type Transaction,
} from "./model.js";
import { paymentEvent } from "./purchases.js";

/** Small deterministic random generator (mulberry32) so the demo looks the same every time. */
function random(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

interface Spend {
  description: string;
  category: string;
  min: number;
  max: number;
}

const EVERYDAY: Array<Spend & { perWeek: number }> = [
  { description: "LOBLAWS #1032", category: "groceries", min: 4000, max: 16000, perWeek: 1.2 },
  { description: "NO FRILLS 3477", category: "groceries", min: 2500, max: 9000, perWeek: 0.8 },
  { description: "COSTCO WHOLESALE W512", category: "groceries", min: 9000, max: 26000, perWeek: 0.25 },
  { description: "TIM HORTONS #2214", category: "dining/coffee", min: 300, max: 1400, perWeek: 2.5 },
  { description: "PHO SAIGON KITCHEN", category: "dining/restaurants", min: 2800, max: 7500, perWeek: 0.5 },
  { description: "PETRO-CANADA 4410", category: "transport/fuel", min: 4500, max: 8500, perWeek: 0.9 },
  { description: "PRESTO AUTOLOAD", category: "transport/transit", min: 2000, max: 4000, perWeek: 0.3 },
  { description: "AMAZON.CA MARKETPLACE", category: "shopping", min: 1500, max: 9000, perWeek: 0.6 },
  { description: "CANADIAN TIRE #188", category: "household/supplies", min: 1200, max: 12000, perWeek: 0.25 },
  { description: "SHOPPERS DRUG MART #921", category: "health/prescriptions", min: 800, max: 6000, perWeek: 0.4 },
  { description: "CINEPLEX ENTERTAINMENT", category: "entertainment/events", min: 1800, max: 4500, perWeek: 0.2 },
];

const MONTHLY: Array<Spend & { day: number }> = [
  { description: "MORTGAGE PAYMENT", category: "housing/mortgage", min: 182_500, max: 182_500, day: 1 },
  { description: "HYDRO ONE NETWORKS", category: "housing/utilities/electricity", min: 9000, max: 17000, day: 8 },
  { description: "ENBRIDGE GAS", category: "housing/utilities/gas", min: 4500, max: 14000, day: 12 },
  { description: "ROGERS WIRELESS", category: "housing/utilities/phone", min: 20_000, max: 20_000, day: 15 },
  { description: "BELL INTERNET", category: "housing/utilities/internet", min: 7500, max: 7500, day: 18 },
  { description: "NETFLIX.COM", category: "entertainment/subscriptions", min: 1649, max: 1649, day: 20 },
  { description: "TFSA CONTRIBUTION", category: "savings", min: 50_000, max: 50_000, day: 2 },
];

function txn(accountId: string, date: IsoDate, amount: number, description: string, category: string | null, n: number): Transaction {
  return { id: `demo-${n}`, accountId, date, amount, description, category, fingerprint: `demo|${date}|${amount}|${description}|${n}` };
}

/** Builds the demo household from the start of last year up to `today`. */
export function demoSnapshot(today: IsoDate): Snapshot {
  const rand = random(20261004);
  const between = (min: number, max: number) => Math.round(min + rand() * (max - min));
  const chequing: Account = { id: "demo-chequing", name: "Everyday chequing", bank: "tangerine" };
  const visa: Account = { id: "demo-visa", name: "Visa", bank: "rbc" };
  const all: Transaction[] = [];
  let n = 0;
  const start = isoDate(yearOf(today) - 1, 1, 1);
  const payments: GroupPayment[] = [];

  for (let date = start; date <= today; date = addDays(date, 1)) {
    const day = Number(date.slice(8, 10));
    const dow = weekday(date);
    // Paid every second Friday.
    if (dow === 5 && Math.round((Date.parse(date) - Date.parse("2025-01-03")) / 86_400_000) % 14 === 0) {
      all.push(txn(chequing.id, date, 248_350, "PAYROLL DEPOSIT NORTHWIND LTD", "income", n++));
    }
    for (const m of MONTHLY) {
      if (day === m.day) all.push(txn(chequing.id, date, -between(m.min, m.max), m.description, m.category, n++));
    }
    for (const e of EVERYDAY) {
      if (rand() < e.perWeek / 7) {
        const account = rand() < 0.7 ? visa : chequing;
        const found = categorize(e.description, -1, STARTER_RULES, new Map());
        all.push(txn(account.id, date, -between(e.min, e.max), e.description, found.category ?? e.category, n++));
      }
    }
    // A few large one-offs make the calendar more interesting.
    if (date.endsWith("-03-14") || date.endsWith("-11-22")) {
      all.push(txn(visa.id, date, -between(60_000, 140_000), "BEST BUY #940", "shopping/electronics", n++));
    }
    if (date.endsWith("-04-28")) all.push(txn(chequing.id, date, 186_000, "CANADA REVENUE AGENCY TAX REFUND", "income", n++));
    // The family pays back its share of the phone plan by e-Transfer. Jo fell behind
    // three months ago and sent one transfer last week that is not recorded yet.
    if (day === 20) {
      for (const who of PHONE_FAMILY) {
        const behind = who.name === "Jo" && date > addMonths(today, -3);
        if (behind) continue;
        const tx = txn(chequing.id, date, 5_000, `INTERAC E-TRANSFER FROM ${who.name.toUpperCase()} ${who.last}`, "transfers-in", n++);
        all.push(tx);
        payments.push({ id: `pay-${tx.id}`, date, from: who.id, to: ME, amount: 5_000, txId: tx.id });
      }
    }
    if (date === addDays(today, -6)) all.push(txn(chequing.id, date, 5_000, "INTERAC E-TRANSFER FROM JO NGUYEN", null, n++));
    // One transaction left uncategorised so the review flow has something to show.
    if (date.endsWith("-09-09")) all.push(txn(visa.id, date, -4_275, "SQ *FARMERS MARKET", null, n++));
  }

  // The car loan has been paid on time.
  const purchases = demoPurchases(today);
  const loanPayments = purchases.flatMap((p) => paymentEvent(p, `Loan payment: ${p.name}`, today) ?? []);
  for (const e of loanPayments) {
    for (const date of occurrences(e, e.date, today)) all.push(txn(chequing.id, date, -e.amount!, "AUTO LOAN PMT NORTHSHORE CREDIT", e.category, n++));
  }

  const months = new Map<string, MonthBundle>();
  for (const t of all) {
    const key = `${t.accountId}|${t.date.slice(0, 7)}`;
    const bundle = months.get(key) ?? { accountId: t.accountId, month: t.date.slice(0, 7), transactions: [] };
    bundle.transactions.push(t);
    months.set(key, bundle);
  }
  return {
    accounts: [chequing, visa],
    months: [...months.values()],
    plan: defaultPlan(538_000),
    learned: {},
    events: [...demoEvents(start), ...loanPayments],
    feed: DEFAULT_FEED,
    profile: { housing: "own", vehicle: "owned", pay: null },
    custom: [],
    notify: DEFAULT_NOTIFY,
    purchases,
    groups: demoGroups(start, today, payments),
  };
}

const PHONE_FAMILY = [
  { id: "demo-sam", name: "Sam", last: "PATEL" },
  { id: "demo-jo", name: "Jo", last: "NGUYEN" },
  { id: "demo-kim", name: "Kim", last: "PATEL" },
];

/** A family phone plan you pay and split four ways, and a cottage weekend split with friends. */
function demoGroups(start: IsoDate, today: IsoDate, payments: GroupPayment[]): Group[] {
  const everyone = [ME, ...PHONE_FAMILY.map((p) => p.id)];
  const phone: Group = {
    id: "00000000-0000-4000-a000-000000000001",
    name: "Family phone plan",
    members: [{ id: ME, name: "" }, ...PHONE_FAMILY.map(({ id, name }) => ({ id, name }))],
    expenses: [],
    recurring: [
      {
        id: "demo-rogers",
        description: "Rogers family plan",
        amount: 20_000,
        paidBy: ME,
        split: { kind: "equal", members: everyone },
        start: addDays(start, 14),
        repeat: "monthly",
        until: null,
      },
    ],
    payments,
    declined: [],
  };
  const cottage: Group = {
    id: "00000000-0000-4000-a000-000000000002",
    name: "Cottage weekend",
    members: [
      { id: ME, name: "" },
      { id: "demo-alex", name: "Alex" },
      { id: "demo-jo", name: "Jo" },
    ],
    expenses: [
      {
        id: "demo-cottage-rent",
        date: addDays(today, -20),
        description: "Cottage rental",
        amount: 90_000,
        paidBy: ME,
        split: { kind: "equal", members: [ME, "demo-alex", "demo-jo"] },
        txId: null,
      },
      {
        id: "demo-cottage-food",
        date: addDays(today, -18),
        description: "Groceries and firewood",
        amount: 21_000,
        paidBy: "demo-alex",
        split: { kind: "shares", parts: { [ME]: 2, "demo-alex": 2, "demo-jo": 1 } },
        txId: null,
      },
    ],
    recurring: [],
    payments: [],
    declined: [],
  };
  return [phone, cottage];
}

/** Two goals being saved for, in priority order, and a car bought on a loan a few months ago. */
function demoPurchases(today: IsoDate): Purchase[] {
  const purchase = (n: number, fields: Partial<Purchase> & Pick<Purchase, "name" | "price" | "targetDate" | "mode">): Purchase => ({
    id: `00000000-0000-4000-9000-${String(n).padStart(12, "0")}`,
    priority: n,
    saved: 0,
    rate: null,
    termMonths: null,
    category: null,
    eventId: null,
    boughtOn: null,
    ...fields,
  });
  return [
    purchase(0, { name: "New roof", price: 1_450_000, targetDate: addMonths(today, 14), mode: "save", saved: 380_000 }),
    purchase(1, { name: "Family trip to the coast", price: 620_000, targetDate: addMonths(today, 8), mode: "save", saved: 95_000 }),
    purchase(2, {
      name: "Hybrid SUV",
      price: 4_200_000,
      targetDate: addMonths(today, -4),
      mode: "finance",
      saved: 800_000,
      rate: 0.0599,
      termMonths: 72,
      category: "transport/loan",
      eventId: "00000000-0000-4000-8000-000000000101",
    }),
  ];
}

function demoEvents(start: IsoDate): CalendarEvent[] {
  const year = yearOf(start);
  const event = (n: number, title: string, date: IsoDate, repeat: CalendarEvent["repeat"], amount: number | null, category: string | null) => ({
    id: `00000000-0000-4000-8000-${String(n).padStart(12, "0")}`,
    title,
    date,
    repeat,
    until: null,
    amount,
    category,
    notes: "",
    hidden: false,
  });
  return [
    event(1, "Mortgage payment", isoDate(year, 1, 1), "monthly", 182_500, "housing/mortgage"),
    event(2, "Rogers bill", isoDate(year, 1, 15), "monthly", 20_000, "housing/utilities/phone"),
    event(3, "Car insurance renewal", isoDate(year, 6, 30), "yearly", 142_000, "transport/insurance"),
    event(4, "Visa payment due", isoDate(year, 1, 31), "monthly", null, null),
    event(5, "Sam's birthday", isoDate(year, 8, 17), "yearly", 7_500, "shopping/gifts"),
    event(6, "Furnace service", isoDate(year + 1, 10, 20), "none", 18_900, "housing/upkeep"),
  ];
}
